/**
 * contact-form.js — every behaviour on the contact form.
 *
 * Five concerns, previously four files and four script tags:
 *
 *   order reference  the order picker fills the free-text order field
 *   account lock     name and email are held to the logged-in customer's account
 *   validation       the browser's native validation bubble, in the page's language
 *   submit state     the Send button acknowledges the click while the captcha runs
 *   sign-in pending  the sign-in link acknowledges the click while the page hands off
 *
 * They are separate init functions, exported individually so each is tested on its own.
 * `init` wires all five and is what the page calls.
 *
 * ORDER MATTERS between the account lock and the submit state, and only between those
 * two. The lock stops propagation when it blocks a submit, so it has to bind first: that
 * is what keeps the spinner from starting on a send that is not happening. This used to
 * depend on the order of script tags in sections/contact-form.liquid, which was easy to
 * break by tidying; it is now a documented sequence of statements in `init` below.
 *
 * Works in both browser (window.ContactForm) and Node.js (module.exports).
 */
(function(exports) {
  'use strict';

  var FORM_SELECTOR = '#ContactForm';

  function resolveRoot(scope) {
    var root = scope || (typeof document !== 'undefined' ? document : null);
    if (!root || typeof root.querySelector !== 'function') return null;

    return root;
  }

  // Taken from the document rather than the global, so listeners bind to the right window
  // when a controller is handed a document directly (which is how the tests drive them).
  function resolveView(root) {
    var view =
      root.defaultView ||
      (root.ownerDocument && root.ownerDocument.defaultView) ||
      (typeof window !== 'undefined' ? window : null);

    return view && typeof view.addEventListener === 'function' ? view : null;
  }

  // ---------------------------------------------------------------------------------
  // Order reference
  //
  // The <select> is a picker only: it carries no name, so it never posts. The text input
  // is the field that reaches our inbox. Picking an order writes it into the input;
  // typing over it releases the picker so the two can never show contradicting values.
  // ---------------------------------------------------------------------------------

  var PICKER_SELECTOR = '[data-contact-order-picker]';
  var ORDER_INPUT_SELECTOR = '[data-contact-order-input]';

  function dispatchInput(element) {
    if (typeof element.dispatchEvent !== 'function') return;

    var view = element.ownerDocument && element.ownerDocument.defaultView;
    if (!view || typeof view.Event !== 'function') return;

    element.dispatchEvent(new view.Event('input', { bubbles: true }));
  }

  function initOrderReference(scope) {
    var root = resolveRoot(scope);
    if (!root) return null;

    var picker = root.querySelector(PICKER_SELECTOR);
    var input = root.querySelector(ORDER_INPUT_SELECTOR);

    // Logged out, or a customer with no orders: the free-text field stands alone.
    if (!picker || !input) return null;

    // Belt and braces. A named picker would post a second, competing order value.
    if (picker.hasAttribute('name')) picker.removeAttribute('name');

    var syncing = false;

    function onPickerChange() {
      syncing = true;
      input.value = picker.value;
      syncing = false;

      dispatchInput(input);
    }

    // Typing by hand wins. Dropping the selection keeps the picker from claiming an order
    // the customer has since edited away from.
    function onInputTyped() {
      if (syncing) return;
      if (picker.value && input.value !== picker.value) picker.value = '';
    }

    picker.addEventListener('change', onPickerChange);
    input.addEventListener('input', onInputTyped);

    return { picker: picker, input: input, sync: onPickerChange };
  }

  // ---------------------------------------------------------------------------------
  // Order reference from a link
  //
  // Order emails link here as /pages/contact?order=IN2021106397, so an enquiry about an
  // order arrives already carrying it. This is deliberately separate from the picker
  // above: the picker only exists for a signed-in customer who has orders, and it has
  // never existed for guests, who are the larger group. The parameter fills the text
  // field, which is the control that actually posts, so it works for everyone.
  // ---------------------------------------------------------------------------------

  var ORDER_PARAM = 'order';

  function orderFromQuery(root) {
    var view = root.defaultView;
    if (!view || !view.location || typeof view.location.search !== 'string') return '';
    if (typeof view.URLSearchParams !== 'function') return '';

    var value = new view.URLSearchParams(view.location.search).get(ORDER_PARAM);
    if (typeof value !== 'string') return '';

    return value.trim();
  }

  function prefillOrderFromQuery(scope) {
    var root = resolveRoot(scope);
    if (!root) return null;

    var input = root.querySelector(ORDER_INPUT_SELECTOR);
    if (!input) return null;

    // Anything already in the field is the customer's, whether they typed it or the
    // back/forward cache restored it. A link never overwrites that.
    if (input.value) return null;

    var value = orderFromQuery(root);
    if (!value) return null;

    // Honour the same ceiling the field advertises, rather than repeating the number.
    var limit = parseInt(input.getAttribute('maxlength'), 10);
    if (limit > 0) value = value.slice(0, limit);

    input.value = value;

    // Where the picker lists the same order, select it, so the two controls cannot sit
    // there disagreeing. Where it does not, the picker stays on its empty prompt and the
    // typed-over rule in initOrderReference keeps working untouched.
    var picker = root.querySelector(PICKER_SELECTOR);
    var matched = false;
    if (picker && picker.options) {
      for (var i = 0; i < picker.options.length; i++) {
        if (picker.options[i].value === value) {
          picker.value = value;
          matched = true;
          break;
        }
      }
    }

    return { input: input, value: value, matchedPicker: matched };
  }

  // ---------------------------------------------------------------------------------
  // Account lock
  //
  // The fields are rendered `readonly`, so this is the backstop rather than the
  // mechanism: it catches what readonly does not, such as an edited DOM, a browser
  // extension, or a value restored from the back/forward cache.
  //
  // This reduces inconsistency; it does not authenticate anyone. Anything posted from a
  // browser can be forged, and Shopify's contact form offers no server-side hook.
  // ---------------------------------------------------------------------------------

  var ACCOUNT_FIELD_SELECTOR = '[data-contact-account-field]';
  var ACCOUNT_ERROR_SELECTOR = '#ContactForm-account-error';

  function accountValue(field) {
    return (field.getAttribute('data-account-value') || '').trim();
  }

  function matchesAccount(field) {
    return (field.value || '').trim() === accountValue(field);
  }

  function initAccountLock(scope) {
    var root = resolveRoot(scope);
    if (!root) return null;

    var form = root.querySelector(FORM_SELECTOR);
    if (!form) return null;

    var fields = Array.prototype.slice.call(root.querySelectorAll(ACCOUNT_FIELD_SELECTOR));

    // Logged out, or an account with nothing to lock against.
    if (!fields.length) return null;

    var error = root.querySelector(ACCOUNT_ERROR_SELECTOR);

    function showError(visible) {
      if (error) error.hidden = !visible;
    }

    function onSubmit(event) {
      var mismatched = fields.filter(function(field) {
        return !matchesAccount(field);
      });

      if (!mismatched.length) {
        showError(false);
        return;
      }

      event.preventDefault();

      // Shopify's hCaptcha script attaches its own submit listener that calls
      // preventDefault unconditionally and then submits the form natively, without ever
      // checking defaultPrevented. Cancelling alone would not actually stop the send, so
      // propagation stops here too. Only on the blocking path: the happy path must reach
      // the captcha listener or nothing would submit at all.
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      mismatched.forEach(function(field) {
        field.value = accountValue(field);
      });

      showError(true);

      // Restored rather than left broken, so the next submit goes straight through.
      if (typeof mismatched[0].focus === 'function') mismatched[0].focus();
    }

    form.addEventListener('submit', onSubmit);

    return { form: form, fields: fields, error: error, validate: onSubmit };
  }

  // ---------------------------------------------------------------------------------
  // Validation messages
  //
  // The bubble a browser shows for a pattern mismatch ("Please match the requested
  // format") is written in the BROWSER's language, not the page's, so a Hong Kong
  // customer browsing in Chinese still got English. setCustomValidity is the only way to
  // override that text.
  //
  // The pattern is evaluated here rather than read off validity.patternMismatch. Browsers
  // compile the pattern attribute with the `v` flag and anchor it at both ends, which is
  // reproduced below.
  // ---------------------------------------------------------------------------------

  var VALIDATION_FIELD_SELECTOR = '[data-validation-message]';

  function compilePattern(pattern) {
    if (!pattern) return null;

    var anchored = '^(?:' + pattern + ')$';

    try {
      return new RegExp(anchored, 'v');
    } catch (error) {
      // Older engines have no `v` flag; a genuinely broken pattern falls through to null
      // below, matching how a browser ignores one it cannot compile.
      try {
        return new RegExp(anchored);
      } catch (fallbackError) {
        return null;
      }
    }
  }

  function setUpValidationField(field) {
    var message = field.getAttribute('data-validation-message');
    var regex = compilePattern(field.getAttribute('pattern'));

    if (!message || !regex || typeof field.setCustomValidity !== 'function') return null;

    function refresh() {
      // An empty optional field is never a pattern mismatch, per the HTML spec.
      if (!field.value) {
        field.setCustomValidity('');
        return;
      }

      field.setCustomValidity(regex.test(field.value) ? '' : message);
    }

    ['input', 'change', 'blur'].forEach(function(eventName) {
      field.addEventListener(eventName, refresh);
    });

    // Covers a value that was already there: server-rendered, autofilled, or restored from
    // the back/forward cache.
    refresh();

    return { field: field, refresh: refresh };
  }

  function initValidation(scope) {
    var root = resolveRoot(scope);
    if (!root) return null;

    var form = root.querySelector(FORM_SELECTOR);
    if (!form) return null;

    var fields = Array.prototype.slice
      .call(form.querySelectorAll(VALIDATION_FIELD_SELECTOR))
      .map(setUpValidationField)
      .filter(Boolean);

    if (!fields.length) return null;

    return { form: form, fields: fields };
  }

  // ---------------------------------------------------------------------------------
  // Submit state
  //
  // Pressing Send is not instant: Shopify's hCaptcha script cancels the native submit,
  // runs its challenge over the network, and only then submits the form itself. That gap
  // left the page looking inert, so the click felt ignored.
  //
  // Nothing here can strand the button: a submit that never completes (a failed captcha,
  // an offline network) is released by a timeout, and returning via the back button
  // clears it.
  // ---------------------------------------------------------------------------------

  var SUBMIT_BUTTON_SELECTOR = '[data-contact-submit]';
  var SUBMIT_STATUS_SELECTOR = '[data-contact-submit-status]';
  var SPINNER_SELECTOR = '.loading__spinner';

  // Long enough that a slow captcha on a poor connection is never cut short, short enough
  // that a customer is not left staring at a dead button.
  var DEFAULT_TIMEOUT_MS = 20000;

  function initSubmitState(scope, options) {
    var root = resolveRoot(scope);
    if (!root) return null;

    var form = root.querySelector(FORM_SELECTOR);
    if (!form) return null;

    var button = form.querySelector(SUBMIT_BUTTON_SELECTOR);
    if (!button) return null;

    var status = form.querySelector(SUBMIT_STATUS_SELECTOR);
    var spinner = button.querySelector(SPINNER_SELECTOR);
    var timeoutMs = (options && options.timeoutMs) || DEFAULT_TIMEOUT_MS;

    var pending = false;
    var timer = null;

    function reset() {
      pending = false;

      if (timer) {
        clearTimeout(timer);
        timer = null;
      }

      button.classList.remove('loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');

      if (spinner) spinner.classList.add('hidden');
      if (status) status.textContent = '';
    }

    function start() {
      pending = true;

      button.classList.add('loading');
      // Not the disabled attribute: that drops focus to the body and stops the button
      // being announced at all, which is the opposite of acknowledging the click.
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');

      if (spinner) spinner.classList.remove('hidden');

      // Set as text rather than revealed, so the live region reliably announces it.
      if (status) {
        status.textContent = status.getAttribute('data-sending-message') || '';
      }

      timer = setTimeout(reset, timeoutMs);
    }

    form.addEventListener('submit', function(event) {
      // Something upstream cancelled it; there is nothing in flight to report.
      if (event.defaultPrevented) return;
      if (pending) return;

      start();
    });

    button.addEventListener('click', function(event) {
      if (pending) event.preventDefault();
    });

    var view = resolveView(root);

    if (view) {
      // Coming back via the back button restores the DOM as it was, spinner and all.
      view.addEventListener('pageshow', function(event) {
        if (event.persisted) reset();
      });
    }

    return { form: form, button: button, status: status, start: start, reset: reset };
  }

  // ---------------------------------------------------------------------------------
  // Sign-in pending
  //
  // "Log in to pick from your orders" leaves for Shopify's hosted login, which is a
  // whole-page navigation to another origin. On a phone that is long enough for the page
  // to look like it ignored the tap, and the second tap just restarts the handoff.
  //
  // The click is never cancelled. This only narrates what the browser is already doing,
  // and covering the link is what stops the second press.
  //
  // Nothing here can strand the overlay. There are three ways down and they cover the
  // three ways this page comes back:
  //
  //   signed in successfully   a FRESH document load, where the markup renders hidden
  //   backed out of the login  a bfcache restore, cleared on pageshow below
  //   navigation never starts  released by the timeout below
  // ---------------------------------------------------------------------------------

  var SIGNIN_LINK_SELECTOR = '[data-contact-signin-link]';
  var SIGNIN_OVERLAY_SELECTOR = '[data-contact-signin-overlay]';

  // Long enough to cover a slow redirect on mobile data, short enough that a handoff that
  // never happened does not leave the form under a spinner the customer cannot clear.
  var DEFAULT_SIGNIN_TIMEOUT_MS = 8000;

  function initSigninPending(scope, options) {
    var root = resolveRoot(scope);
    if (!root) return null;

    // Both only exist while logged out, and they are rendered together. Either one
    // missing means there is no handoff on this page to narrate.
    var link = root.querySelector(SIGNIN_LINK_SELECTOR);
    if (!link) return null;

    var overlay = root.querySelector(SIGNIN_OVERLAY_SELECTOR);
    if (!overlay) return null;

    var timeoutMs = (options && options.signinTimeoutMs) || DEFAULT_SIGNIN_TIMEOUT_MS;

    var timer = null;

    function hide() {
      // Cleared as well as stopped: a timer left running from an earlier press would
      // otherwise come due on top of a later one and take that overlay down with it.
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }

      overlay.hidden = true;
    }

    function show() {
      if (timer) clearTimeout(timer);

      overlay.hidden = false;
      timer = setTimeout(hide, timeoutMs);
    }

    // No preventDefault, and no guard against a repeat press: the second click has to
    // reach the browser too, or an impatient customer cancels their own navigation.
    link.addEventListener('click', function() {
      show();
    });

    var view = resolveView(root);

    if (view) {
      view.addEventListener('pageshow', function(event) {
        if (!event.persisted) return;

        hide();

        // The page comes back exactly as it left, which includes the link still holding
        // focus, so it returns looking pressed. Some browsers restore focus after this
        // event, hence the second pass on the next tick.
        var doc = root.ownerDocument || root;
        var drop = function() {
          var el = doc.activeElement;
          if (el && el !== doc.body && typeof el.blur === 'function') el.blur();
        };

        drop();
        setTimeout(drop, 0);
      });
    }

    return { link: link, overlay: overlay, show: show, hide: hide };
  }

  // ---------------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------------

  function init(scope, options) {
    var root = resolveRoot(scope);
    if (!root) return null;

    if (!root.querySelector(FORM_SELECTOR)) return null;

    var orderReference = initOrderReference(root);

    // After initOrderReference, so its listeners are already bound when a link-supplied
    // value lands in the field.
    var orderPrefill = prefillOrderFromQuery(root);

    var validation = initValidation(root);

    // The account lock binds BEFORE the submit state, deliberately. Listeners on the same
    // element run in registration order, and the lock calls stopImmediatePropagation when
    // it blocks, which stops the submit state from starting a spinner for a send that is
    // not going to happen. Do not reorder these two.
    var accountLock = initAccountLock(root);
    var submitState = initSubmitState(root, options);

    // Independent of the three above: it binds to the sign-in link, which only exists
    // while logged out, and touches nothing the form's own submit path uses.
    var signinPending = initSigninPending(root, options);

    return {
      orderReference: orderReference,
      orderPrefill: orderPrefill,
      validation: validation,
      accountLock: accountLock,
      submitState: submitState,
      signinPending: signinPending,
    };
  }

  function autoInit() {
    if (typeof document === 'undefined') return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        init(document);
      });
    } else {
      init(document);
    }
  }

  exports.init = init;
  exports.initOrderReference = initOrderReference;
  exports.prefillOrderFromQuery = prefillOrderFromQuery;
  exports.initAccountLock = initAccountLock;
  exports.initValidation = initValidation;
  exports.initSubmitState = initSubmitState;
  exports.initSigninPending = initSigninPending;
  exports.autoInit = autoInit;

  if (typeof window !== 'undefined') {
    window.ContactForm = exports;
    autoInit();
  }
})(typeof module !== 'undefined' && module.exports ? module.exports : {});
